import React, { useState, useEffect } from 'react';
import {
    Save, X, Plus, Trash2, Search, Database, Clock, Calculator,
    Zap, BookOpen, Crown, AlertTriangle, HeartHandshake, Sparkles, Moon, Sun, Briefcase,
    RotateCcw
} from 'lucide-react';
import {
  TYPE_ICONS, INGREDIENT_ICONS, MAIN_SKILL_ICONS, TYPE_TO_BERRY,
  formatIngredientName, formatSkillName
} from '../utils/gameData';

// NEW: The hidden base strength values used to calculate ingredient quotas
const INGREDIENT_STRENGTH = {
  "FANCY_APPLE": 90,
  "MOOMOO_MILK": 98,
  "GREENGRASS_SOYBEANS": 100,
  "HONEY": 101,
  "BEAN_SAUSAGE": 103,
  "WARMING_GINGER": 109,
  "SNOOZY_TOMATO": 110,
  "FANCY_EGG": 115,
  "PURE_OIL": 121,
  "SOFT_POTATO": 124,
  "FIERY_HERB": 130,
  "GREENGRASS_CORN": 140,
  "SOOTHING_CACAO": 151,
  "ROUSING_COFFEE": 153,
  "GLOSSY_AVOCADO": 162,
  "TASTY_MUSHROOM": 167,
  "LARGE_LEEK": 185,
  "PLUMP_PUMPKIN": 250,
  "SLOWPOKE_TAIL": 342
};

export default function ConfigModal({ pokedex, overrides, onClose, onSavePokedex, onDeletePokedex, onResetPokedex }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [formData, setFormData] = useState(null);

  const [freqH, setFreqH] = useState(0);
  const [freqM, setFreqM] = useState(0);
  const [freqS, setFreqS] = useState(0);

  // The override record for the currently selected species
  const currentOverride = selectedId && selectedId !== 'NEW' ? (overrides[selectedId] || null) : null;
  const editedFields = new Set(currentOverride?._edited_fields || []);
  const isNew = currentOverride?._is_new || false;

  // Returns true if the field has been modified from the base pokedex.json
  const isEdited = (field) => editedFields.has(field);

  // CSS classes to apply to fields that have been edited
  const editedCls = "ring-2 ring-amber-400 bg-amber-50";
  const baseCls = "bg-gray-50";
  const fieldCls = (field) => isEdited(field) ? editedCls : baseCls;

  useEffect(() => {
    if (selectedId && pokedex[selectedId]) {
      const data = { ...pokedex[selectedId] };
      setFormData(data);
      parseFrequency(data.frequency);
    } else if (selectedId === 'NEW') {
      const defaultData = {
        id: "new_mon",
        name: "",
        pokedexNumber: 9999,
        type: "Normal",
        specialty: "berry",
        berry: "Persim",
        frequency: "0,50,0",
        ingredientPercentage: 20,
        skillPercentage: 2,
        skill: "Metronome",
        carrySize: 10,
        ingredientPools: {
            "0": [{ name: "FANCY_APPLE", count: 1 }],
            "30": [{ name: "FANCY_APPLE", count: 2 }],
            "60": [{ name: "FANCY_APPLE", count: 4 }]
        },
        family: "new_mon",
        final_evolution: "new_mon"
      };
      setFormData(defaultData);
      parseFrequency("0,50,0");
    }
  }, [selectedId, pokedex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const parseFrequency = (freq) => {
    let h = 0, m = 0, s = 0;
    if (typeof freq === 'string' && freq.includes(',')) {
      const parts = freq.split(',').map(Number);
      h = parts[0] || 0;
      m = parts[1] || 0;
      s = parts[2] || 0;
    } else {
      const seconds = Number(freq) || 3600;
      h = Math.floor(seconds / 3600);
      m = Math.floor((seconds % 3600) / 60);
      s = seconds % 60;
    }
    setFreqH(h);
    setFreqM(m);
    setFreqS(s);
  };

  const updateFrequency = (h, m, s) => {
    const safeH = parseInt(h || 0);
    const safeM = parseInt(m || 0);
    const safeS = parseInt(s || 0);

    setFreqH(h);
    setFreqM(m);
    setFreqS(s);

    setFormData(prev => ({ ...prev, frequency: `${safeH},${safeM},${safeS}` }));
  };

  const handleSave = async () => {
    if (!formData) return;
    await onSavePokedex(formData);
  };

  const handleDelete = async () => {
    if (selectedId === 'NEW') {
        setSelectedId(null);
        setFormData(null);
    } else {
        await onDeletePokedex(selectedId);
        setSelectedId(null);
        setFormData(null);
    }
  };

  const handleReset = async () => {
    if (!selectedId || selectedId === 'NEW') return;
    await onResetPokedex(selectedId);
    // formData will auto-update via the useEffect when pokedex prop changes
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    const autoId = newName.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

    setFormData(prev => {
        const isNew = selectedId === 'NEW';
        const currentIdMatchesName = prev.id === prev.name.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

        return {
            ...prev,
            name: newName,
            id: (isNew || currentIdMatchesName || prev.id === 'new_mon') ? autoId : prev.id,
            family: (isNew && (prev.family === 'new_mon' || prev.family === prev.id)) ? autoId : prev.family,
            final_evolution: (isNew && (prev.final_evolution === 'new_mon' || prev.final_evolution === prev.id)) ? autoId : prev.final_evolution
        };
    });
  };

  const handleTypeChange = (type) => {
    const berry = TYPE_TO_BERRY[type] || "Persim";
    setFormData(prev => ({ ...prev, type, berry: berry.toUpperCase() }));
  };

  // UPDATE: Now automatically balances the quota when you change ingredients
  const updatePool = (level, action, index, value) => {
    const newPools = { ...formData.ingredientPools };
    const pool = [...(newPools[level] || [])];

    if (action === 'update') {
        const oldItem = pool[index];
        let newItem = { ...oldItem, ...value };

        // If the user changed the ingredient NAME, auto-calculate the correct COUNT
        if (value.name && oldItem.name !== value.name) {
             // Find the Target Energy Quota based on the first slot in this level
             const baselineItem = (index !== 0 && pool[0]) ? pool[0] : oldItem;
             const baselineStrength = INGREDIENT_STRENGTH[baselineItem.name] || 100;
             const targetQuota = baselineStrength * baselineItem.count;

             // Divide the Quota by the new ingredient's base strength
             const newStrength = INGREDIENT_STRENGTH[value.name] || 100;
             newItem.count = Math.max(1, Math.round(targetQuota / newStrength));
        }

        pool[index] = newItem;
    } else if (action === 'add') {
        let defaultCount = 1;
        // Automatically calculate the correct starting amount for an Apple based on this level's quota
        if (pool.length > 0) {
             const baselineItem = pool[0];
             const targetQuota = (INGREDIENT_STRENGTH[baselineItem.name] || 100) * baselineItem.count;
             defaultCount = Math.max(1, Math.round(targetQuota / INGREDIENT_STRENGTH["FANCY_APPLE"]));
        }
        pool.push({ name: "FANCY_APPLE", count: defaultCount });
    } else if (action === 'remove') {
        pool.splice(index, 1);
    }

    newPools[level] = pool;
    setFormData({ ...formData, ingredientPools: newPools });
  };

  const filteredList = Object.values(pokedex)
    .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a,b) => a.name.localeCompare(b.name));

  // Count total overrides for the header badge
  const overrideCount = Object.values(overrides).filter(o => !o._deleted).length;

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[85vh] flex flex-col overflow-hidden ring-1 ring-gray-900/5" onClick={(e) => e.stopPropagation()}>

        {/* PERSISTENT HEADER */}
        <div className="bg-gray-800 p-4 flex justify-between items-center text-white shrink-0 shadow-md z-20">
          <div className="flex items-center gap-3">
            <Database size={20} className="text-indigo-400" />
            <h2 className="text-lg md:text-xl font-black tracking-tight">Pokedex Database</h2>
            {overrideCount > 0 && (
              <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-black rounded-full">
                {overrideCount} edited
              </span>
            )}
          </div>

          <button onClick={onClose} className="p-2 bg-gray-700 hover:bg-red-500 rounded-lg transition-colors text-gray-300 hover:text-white shadow-sm" title="Close Settings">
            <X size={20} />
          </button>
        </div>

        {/* CONTENT AREA */}
        <div className="flex-grow flex overflow-hidden bg-white">

                    {/* LEFT: Sidebar List */}
                    <div className="w-72 bg-gray-50 border-r border-gray-200 flex flex-col shrink-0 z-10">
                        <div className="p-3 bg-white border-b border-gray-200">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search species..."
                                    className="w-full pl-8 pr-2 py-2 bg-gray-100 border border-gray-200 rounded-lg text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none"
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                                <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                            </div>
                        </div>

                        <div className="flex-grow overflow-y-auto p-3 space-y-1.5 custom-scrollbar">
                            <button
                                onClick={() => setSelectedId('NEW')}
                                className={`w-full text-left px-3 py-2.5 rounded-lg text-xs font-black flex items-center gap-2 transition-all shadow-sm
                                    ${selectedId === 'NEW' ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200 text-indigo-600 hover:bg-indigo-50'}
                                `}
                            >
                                <Plus size={14} /> ADD NEW SPECIES
                            </button>

                            <div className="h-2"></div>

                            {filteredList.map(p => {
                                const ov = overrides[p.id];
                                const hasOverride = ov && !ov._deleted;
                                const isCustomNew = hasOverride && ov._is_new;
                                return (
                                    <button
                                        key={p.id}
                                        onClick={() => setSelectedId(p.id)}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition-colors
                                            ${selectedId === p.id ? 'bg-indigo-100 text-indigo-700 shadow-sm border border-indigo-200' : 'text-gray-600 hover:bg-gray-100 border border-transparent'}
                                        `}
                                    >
                                        <span className="flex-1 truncate">{p.name}</span>
                                        {hasOverride && (
                                            <span
                                                className={`shrink-0 w-2 h-2 rounded-full ${isCustomNew ? 'bg-green-400' : 'bg-amber-400'}`}
                                                title={isCustomNew ? 'Custom species' : `${ov._edited_fields?.length || 0} field(s) edited`}
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* RIGHT: Form Area */}
                    <div className="flex-grow flex flex-col bg-white overflow-hidden relative">
                        {formData ? (
                            <>
                                <div className="flex-grow overflow-y-auto p-8 custom-scrollbar">
                                    <div className="max-w-3xl mx-auto space-y-8 pb-10">

                                        {/* Override status banner */}
                                        {currentOverride && !isNew && editedFields.size > 0 && (
                                            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs font-bold text-amber-700">
                                                <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                                                {editedFields.size} field{editedFields.size !== 1 ? 's' : ''} edited from base data.
                                                Highlighted fields show your changes.
                                            </div>
                                        )}
                                        {isNew && (
                                            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs font-bold text-green-700">
                                                <span className="w-2 h-2 rounded-full bg-green-400 shrink-0" />
                                                Custom species — not in base data.
                                            </div>
                                        )}

                                        {/* Identity Section */}
                                        <div className="space-y-4">
                                            <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest border-b pb-2">Identity</h3>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                                        Name {isEdited('name') && <span className="text-amber-500">(edited)</span>}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={formData.name}
                                                        onChange={handleNameChange}
                                                        placeholder="e.g. Cutiefly"
                                                        className={`w-full p-2 border rounded-lg font-bold focus:ring-2 focus:ring-indigo-500 outline-none ${fieldCls('name')}`}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                                        Pokédex # {isEdited('pokedexNumber') && <span className="text-amber-500">(edited)</span>}
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={formData.pokedexNumber || ""}
                                                        onChange={e => setFormData({...formData, pokedexNumber: parseInt(e.target.value) || 0})}
                                                        className={`w-full p-2 border rounded-lg font-bold focus:ring-2 focus:ring-indigo-500 outline-none ${fieldCls('pokedexNumber')}`}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">ID (File Name)</label>
                                                    <input
                                                        type="text"
                                                        value={formData.id}
                                                        onChange={e => setFormData({...formData, id: e.target.value})}
                                                        className="w-full p-2 bg-gray-50 border rounded-lg font-mono text-xs text-indigo-600"
                                                    />
                                                    <p className="text-[10px] text-gray-400 mt-1">Must match image filename (e.g. "cutiefly" for cutiefly.png)</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                                        Type {isEdited('type') && <span className="text-amber-500">(edited)</span>}
                                                    </label>
                                                    <select value={formData.type} onChange={e => handleTypeChange(e.target.value)} className={`w-full p-2 border rounded-lg font-bold ${fieldCls('type')}`}>
                                                        {Object.keys(TYPE_ICONS).map(t => <option key={t} value={t}>{t}</option>)}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                                        Specialty {isEdited('specialty') && <span className="text-amber-500">(edited)</span>}
                                                    </label>
                                                    <select value={formData.specialty} onChange={e => setFormData({...formData, specialty: e.target.value})} className={`w-full p-2 border rounded-lg font-bold capitalize ${fieldCls('specialty')}`}>
                                                        {["berry", "ingredient", "skill", "all"].map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Stats Section */}
                                        <div className="space-y-4">
                                            <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest border-b pb-2">Base Stats</h3>
                                            <div className="grid grid-cols-4 gap-4">

                                                <div className="col-span-2">
                                                    <label className="block text-xs font-bold text-gray-500 mb-1 flex items-center gap-1">
                                                        <Clock size={12}/> Frequency {isEdited('frequency') && <span className="text-amber-500">(edited)</span>}
                                                    </label>
                                                    <div className="flex gap-2 items-center">
                                                        <div className="flex-1 relative">
                                                            <input type="number" value={freqH} onChange={e => updateFrequency(e.target.value, freqM, freqS)} className={`w-full p-2 border rounded-lg font-bold text-center outline-none focus:ring-2 focus:ring-indigo-500 ${fieldCls('frequency')}`}/>
                                                            <span className="absolute right-2 top-2 text-xs font-bold text-gray-400">h</span>
                                                        </div>
                                                        <div className="flex-1 relative">
                                                            <input type="number" value={freqM} onChange={e => updateFrequency(freqH, e.target.value, freqS)} className={`w-full p-2 border rounded-lg font-bold text-center outline-none focus:ring-2 focus:ring-indigo-500 ${fieldCls('frequency')}`}/>
                                                            <span className="absolute right-2 top-2 text-xs font-bold text-gray-400">m</span>
                                                        </div>
                                                        <div className="flex-1 relative">
                                                            <input type="number" value={freqS} onChange={e => updateFrequency(freqH, freqM, e.target.value)} className={`w-full p-2 border rounded-lg font-bold text-center outline-none focus:ring-2 focus:ring-indigo-500 ${fieldCls('frequency')}`}/>
                                                            <span className="absolute right-2 top-2 text-xs font-bold text-gray-400">s</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-span-2">
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                                        Carry Limit {isEdited('carrySize') && <span className="text-amber-500">(edited)</span>}
                                                    </label>
                                                    <input type="number" value={formData.carrySize} onChange={e => setFormData({...formData, carrySize: parseInt(e.target.value)})} className={`w-full p-2 border rounded-lg font-bold ${fieldCls('carrySize')}`} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                                        Ing Rate (%) {isEdited('ingredientPercentage') && <span className="text-amber-500">(edited)</span>}
                                                    </label>
                                                    <input type="number" step="0.1" value={formData.ingredientPercentage} onChange={e => setFormData({...formData, ingredientPercentage: parseFloat(e.target.value)})} className={`w-full p-2 border rounded-lg font-bold ${fieldCls('ingredientPercentage')}`} />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                                        Skill Rate (%) {isEdited('skillPercentage') && <span className="text-amber-500">(edited)</span>}
                                                    </label>
                                                    <input type="number" step="0.1" value={formData.skillPercentage} onChange={e => setFormData({...formData, skillPercentage: parseFloat(e.target.value)})} className={`w-full p-2 border rounded-lg font-bold ${fieldCls('skillPercentage')}`} />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                                        Main Skill {isEdited('skill') && <span className="text-amber-500">(edited)</span>}
                                                    </label>
                                                    <select value={formData.skill} onChange={e => setFormData({...formData, skill: e.target.value})} className={`w-full p-2 border rounded-lg font-bold ${fieldCls('skill')}`}>
                                                        {Object.keys(MAIN_SKILL_ICONS).sort().map(s => <option key={s} value={s}>{formatSkillName(s)}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Ingredient Pools */}
                                        <div className="space-y-4">
                                            <h3 className={`text-sm font-black uppercase tracking-widest border-b pb-2 ${isEdited('ingredientPools') ? 'text-amber-500' : 'text-gray-400'}`}>
                                                Ingredient Options {isEdited('ingredientPools') && <span className="text-amber-500">(edited)</span>}
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                {["0", "30", "60"].map(lvl => (
                                                    <div key={lvl} className={`rounded-xl p-3 border ${isEdited('ingredientPools') ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                                                        <div className="text-xs font-black text-gray-400 mb-2">Level {lvl === "0" ? "1" : lvl}</div>
                                                        <div className="space-y-2">
                                                            {(formData.ingredientPools[lvl] || []).map((slot, idx) => (
                                                                <div key={idx} className="flex gap-2 items-center">
                                                                    <select
                                                                        value={slot.name}
                                                                        onChange={(e) => updatePool(lvl, 'update', idx, {name: e.target.value})}
                                                                        className="flex-grow p-1.5 text-xs font-bold border rounded outline-none w-0 bg-white"
                                                                    >
                                                                        {Object.keys(INGREDIENT_ICONS).map(i => <option key={i} value={i}>{formatIngredientName(i)}</option>)}
                                                                    </select>
                                                                    <input
                                                                        type="number"
                                                                        min="1" max="20"
                                                                        value={slot.count}
                                                                        onChange={(e) => updatePool(lvl, 'update', idx, {count: parseInt(e.target.value)})}
                                                                        className="w-12 p-1.5 text-xs font-bold border rounded text-center outline-none bg-white"
                                                                    />
                                                                    <button
                                                                        onClick={() => updatePool(lvl, 'remove', idx)}
                                                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                                                                    >
                                                                        <Trash2 size={12} />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            <button
                                                                onClick={() => updatePool(lvl, 'add')}
                                                                className="w-full py-1.5 border-2 border-dashed border-gray-300 rounded text-xs font-bold text-gray-400 hover:border-indigo-400 hover:text-indigo-500 transition-colors"
                                                            >
                                                                + Add Option
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Evolution Linking */}
                                        <div className="space-y-4">
                                            <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest border-b pb-2">Evolution Chain</h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                                        Family ID (Group) {isEdited('family') && <span className="text-amber-500">(edited)</span>}
                                                    </label>
                                                    <input type="text" value={formData.family || ""} onChange={e => setFormData({...formData, family: e.target.value})} className={`w-full p-2 border rounded-lg font-mono text-xs ${fieldCls('family')}`} />
                                                    <p className="text-[10px] text-gray-400 mt-1">Share this ID with all evolutions (e.g. "pikachu")</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1">
                                                        Final Evolution ID {isEdited('final_evolution') && <span className="text-amber-500">(edited)</span>}
                                                    </label>
                                                    <input type="text" value={formData.final_evolution || ""} onChange={e => setFormData({...formData, final_evolution: e.target.value})} className={`w-full p-2 border rounded-lg font-mono text-xs ${fieldCls('final_evolution')}`} />
                                                    <p className="text-[10px] text-gray-400 mt-1">ID of the fully evolved form (e.g. "raichu")</p>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                {/* Form Footer */}
                                <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between gap-3 shrink-0 absolute bottom-0 w-full z-20">
                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleDelete}
                                            className="px-4 py-2 text-red-500 font-bold hover:bg-red-50 rounded-lg flex items-center gap-2 shadow-sm"
                                        >
                                            <Trash2 size={16} /> Delete
                                        </button>
                                        {currentOverride && pokedex[selectedId] && (
                                            <button
                                                onClick={handleReset}
                                                className="px-4 py-2 text-amber-600 font-bold hover:bg-amber-50 rounded-lg flex items-center gap-2 shadow-sm"
                                                title="Remove all edits and restore base data"
                                            >
                                                <RotateCcw size={16} /> Reset to Default
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => { setSelectedId(null); setFormData(null); }} className="px-4 py-2 font-bold text-gray-600 bg-white border border-gray-200 hover:bg-gray-100 rounded-lg shadow-sm">Discard Changes</button>
                                        <button onClick={handleSave} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold shadow-lg shadow-indigo-200 flex items-center gap-2">
                                            <Save size={16} /> Save Species
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex-grow flex flex-col items-center justify-center text-gray-300">
                                <Database size={64} className="mb-4 opacity-50" />
                                <p className="font-bold text-lg">Select a Pokémon to edit</p>
                                <p className="text-sm">Or click "Add New Species" to create one</p>
                            </div>
                        )}
                    </div>
        </div>
      </div>
    </div>
  );
}
